import Ember from 'ember';
import Service, { inject as service } from '@ember/service';
import ApiService from './api';
import { action } from '@ember/object';
import AccountService from './account';
import { getExperimentVariation } from '@customerio/hydra-experiments';
import config from 'cio/config/environment';

// This is a list of all experiments that we are running, along with their
// variations and each variation's weight.
// The weight is a number between 0 and 1 that represents the percentage
// of users that should be assigned to that variation.
// The criteria is a string that represents how the test should be bucketed.
const EXPERIMENTS = {
  welcomeMessageExperiment: {
    variations: [
      { name: 'welcome_message_control', weight: 0 },
      { name: 'welcome_message_variation_a', weight: 1 },
    ],
    criteria: 'account',
  },
  fakeDoorExperiment: {
    variations: [
      { name: 'fake_door_control', weight: 0.5 },
      { name: 'fake_door_test_group', weight: 0.5 },
    ],
    criteria: 'account',
  },
  campaignTemplateGalleryExperiment: {
    variations: [
      {
        name: 'campaign_template_gallery_control',
        weight: 0.5,
      },
      {
        name: 'campaign_template_gallery_variation_a',
        weight: 0.5,
      },
    ],
    criteria: 'account',
  },
  testDeliveryMode: {
    variations: [
      { name: 'control', weight: 1 },
      { name: 'treatment', weight: 0 },
    ],
    criteria: 'random',
  },
} as const satisfies Record<string, Experiment>;

type ExperimentCriteria = 'account' | 'user' | 'random';

interface Experiment {
  variations: readonly ExperimentVariation[];
  criteria: ExperimentCriteria;
}

interface ExperimentVariation {
  name: string;
  weight: number;
}

type Experiments = typeof EXPERIMENTS;
type ExperimentName = keyof Experiments;
type VariationsOf<T extends ExperimentName> = Experiments[T]['variations'];
type VariationOf<T extends ExperimentName> = VariationsOf<T>[number]['name'];

export default class ExperimentsService extends Service {
  @service api!: ApiService;
  @service account!: AccountService;

  generateIdentifier(criteria: ExperimentCriteria): string {
    switch (criteria) {
      case 'account': {
        let accountId = this.account.current?.id;
        if (accountId == null || accountId === '') {
          if (config.isDevelopment || Ember.testing) {
            // eslint-disable-next-line no-console
            console.error(
              `No account ID found for experiment. ${
                Ember.testing ? `If you are running tests, make sure to set up an account and that it has an ID.` : ''
              }`,
            );
          }
          return '0';
        }
        return accountId;
      }
      case 'user': {
        let userId = this.account.current?.currentUser?.id;
        if (userId == null || userId === '') {
          if (config.isDevelopment || Ember.testing) {
            // eslint-disable-next-line no-console
            console.error(
              `No user ID found for experiment. ${
                Ember.testing
                  ? `If you are running tests, make sure to set up a user on \`account.current.currentUser\`, and that it has an ID.`
                  : ''
              }`,
            );
          }
          return '0';
        }
        return userId;
      }
      case 'random':
        return Math.random().toString();
      default:
        return '0';
    }
  }

  getLocalStorageKey<T extends ExperimentName>(name: T): string {
    return `experiment-${name}`;
  }

  // "Forced test variations" are a way of setting a specific test variation
  // manually. Should be allowed only on dev environments.
  get shouldAllowForcedTestVariations(): boolean {
    return (
      config.isStandalone ||
      config.cioEnvironment === 'development' ||
      config.cioEnvironment === 'devbox' ||
      Ember.testing
    );
  }

  getForcedTestVariation<T extends ExperimentName>(name: T): VariationOf<T> | null {
    if (this.shouldAllowForcedTestVariations === false) {
      return null;
    }

    let localStorageKey = this.getLocalStorageKey(name);
    let forcedTestVariation = localStorage.getItem(localStorageKey);
    return forcedTestVariation as VariationOf<T> | null;
  }

  get hasForcedTestVariations(): boolean {
    let keys = Object.keys(EXPERIMENTS) as ExperimentName[];

    return keys.some((name) => {
      return this.getForcedTestVariation(name) != null;
    });
  }

  storeTestVariation<T extends ExperimentName>(name: T, value: VariationOf<T>): void {
    let localStorageKey = this.getLocalStorageKey(name);

    localStorage.setItem(localStorageKey, value);
  }

  @action
  getExperimentVariation<T extends ExperimentName>(name: T): VariationOf<T> {
    let forcedTestVariation = this.getForcedTestVariation(name);
    if (forcedTestVariation != null) {
      return forcedTestVariation;
    }

    let experimentData = EXPERIMENTS[name];

    let variation = getExperimentVariation({
      name,
      variations: experimentData.variations.map(({ name, weight }) => ({
        id: name,
        split: weight,
      })),
      identifier: this.generateIdentifier(experimentData.criteria),
    });

    return variation;
  }

  @action
  resetForcedTestVariations(): void {
    let keys = Object.keys(EXPERIMENTS) as ExperimentName[];

    keys.forEach((name) => {
      let localStorageKey = this.getLocalStorageKey(name);

      localStorage.removeItem(localStorageKey);
    });
  }

  get allExperiments() {
    let keys = Object.keys(EXPERIMENTS) as ExperimentName[];

    return keys.map((name) => {
      return { name, ...EXPERIMENTS[name] };
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    experiments: ExperimentsService;
  }
}
